import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Subscription.css';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { subscriptionPlanService } from '../../services';

const Subscription = () => {
  const navigate = useNavigate();

  const handleGetStarted = (planName) => {
    // Check if user is logged in
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      // User is logged in, could navigate to payment or dashboard
      alert(`Selecting ${planName} plan. Payment integration would go here.`);
      // navigate('/payment', { state: { plan: planName } });
    } else {
      // User not logged in, redirect to login
      navigate('/login');
    }
  };

  const [activePlans, setActivePlans] = useState(null);
  const [loadingActive, setLoadingActive] = useState(false);
  const [activeError, setActiveError] = useState(null);

  // Helper to normalize feature entries which may come as strings or objects
  const getFeatureText = (feature) => {
    if (!feature && feature !== 0) return '';
    if (typeof feature === 'string') return feature;
    if (typeof feature === 'object') {
      return feature.text || feature.name || feature.title || feature.label || '';
    }
    return String(feature);
  };

  const getFeatureIncluded = (feature) => {
    if (feature == null) return false;
    if (typeof feature === 'string') return true; // assume string features are included
    if (typeof feature === 'object') {
      return feature.included ?? feature.isIncluded ?? feature.enabled ?? true;
    }
    return Boolean(feature);
  };

  // Map plan name/title to the existing button CSS classes so backend-driven plans keep the original colors
  const mapPlanNameToButtonClass = (name) => {
    if (!name) return '';
    const n = String(name).toLowerCase();
    if (n.includes('trial')) return 'trial-btn';
    if (n.includes('free')) return 'freebie-btn';
    if (n.includes('plus')) return 'plus-btn';
    if (n.includes('premium')) return 'premium-btn';
    // fallback: use popular/primary style if name suggests professional or pro
    if (n.includes('pro') || n.includes('professional')) return 'plus-btn';
    return '';
  };

  // Format price: accept numbers or numeric strings and return formatted string with thousands separators and currency suffix (đ)
  const formatPrice = (raw) => {
    if (raw == null) return '';
    // If already contains non-digit characters, return as-is
    const s = String(raw).trim();
    // If it already contains comma or dot separators or currency symbol, return as-is
    if (/[,\.\sđ₫€$£]/.test(s)) return s;
    // Try to parse number
    const n = Number(s.replace(/[^0-9-]/g, ''));
    if (Number.isNaN(n)) return s;
    return n.toLocaleString('en-US') + '\u0111';
  };

  // Determine a normalized period string (e.g. '/Month', '/Year', '/1 Day') from various possible backend fields
  const getPlanPeriod = (plan) => {
    if (!plan) return '';

    const candidates = [
      plan.period,
      plan.periodText,
      plan.billingCycle,
      plan.billingPeriod,
      plan.frequency,
      plan.interval,
      plan.duration,
      plan.durationText,
      plan.billingFrequency,
      plan.periodicity,
      plan.paymentInterval,
    ];

    for (const c of candidates) {
      if (!c && c !== 0) continue;
      const s = String(c).trim();
      if (!s) continue;
      const lower = s.toLowerCase();
      // common normalizations
      if (/\b1\s*day\b/.test(lower) || /\bonce\s*day\b/.test(lower)) return '/1 Day';
      if (/\bday(s)?\b/.test(lower) && /\d+/.test(lower)) return '/' + s;
      if (/month|monthly|per month|/i.test(lower) || /\bmo\b/.test(lower)) return '/Month';
      if (/year|annual|annually|per year|yr\b/.test(lower)) return '/Year';
      // if it's already prefixed with '/', keep it
      if (s.startsWith('/')) return s;
      // simple numeric like '1' or '12' — return as '/{s}'
      if (/^\d+$/.test(s)) return '/' + s;
      // fallback: return with leading slash
      return '/' + s;
    }

    // Try scanning other stringy fields in plan for words 'month' or 'year'
    try {
      const json = JSON.stringify(plan).toLowerCase();
      if (json.includes('month')) return '/Month';
      if (json.includes('year')) return '/Year';
      if (json.includes('day')) return '/1 Day';
    } catch (e) {
      // ignore
    }

    return '';
  };

  useEffect(() => {
    let mounted = true;
    const loadActive = async () => {
      setLoadingActive(true);
      setActiveError(null);
      try {
        const data = await subscriptionPlanService.getActive();
        if (!mounted) return;
        // Expect data to be an array of plans
        setActivePlans(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Failed to load active subscription plans', err);
        if (!mounted) return;
        setActiveError(err);
        setActivePlans([]);
      } finally {
        if (mounted) setLoadingActive(false);
      }
    };
    loadActive();
    return () => { mounted = false; };
  }, []);

  const plans = [
    {
      name: 'Trial 1 Day',
      description: 'Try all premium features for 1 day, then decide.',
      price: '0đ',
      period: '/1 Day',
      buttonText: 'Start Free Trial',
      buttonClass: 'trial-btn',
      isHighlighted: true,
      features: [
        { text: 'Full access to all therapy sessions', included: true },
        { text: 'Unlock all advanced mental health lessons', included: true },
        { text: 'Access to all premium community features', included: true },
        { text: 'AI companion for mental health tracking', included: true },
        { text: 'Priority booking with top therapists', included: true },
        { text: '24/7 direct support', included: true },
        { text: 'Cancel anytime during trial period', included: true }
      ]
    },
    {
      name: 'Free',
      description: 'Ideal for beginners, experience mental health support.',
      price: '0đ',
      period: '/Month',
      buttonText: 'Get Started Now',
      buttonClass: 'freebie-btn',
      features: [
        { text: '30-minute trial session with a therapist', included: true },
        { text: 'Basic mental health lessons', included: true },
        { text: 'No access to in-depth therapy sessions', included: false },
        { text: 'No access to community rewards', included: false }
      ]
    },
    {
      name: 'Plus +',
      description: 'Individuals needing more advanced support and tools.',
      price: '249,000đ',
      period: '/Month',
      buttonText: 'Get Started Now',
      buttonClass: 'plus-btn',
      isPopular: true,
      features: [
        { text: '15 free therapy sessions/month', included: true },
        { text: 'Unlock all advanced mental health lessons', included: true },
        { text: 'Access to starter gift skins in the community', included: true },
        { text: 'AI to track your mental health after each session', included: true },
        { text: 'Anonymous chat enhanced community access', included: true },
        { text: 'Option to book your favorite therapist', included: true },
        { text: 'No premium privileges in the community', included: false }
      ]
    },
    {
      name: 'Premium',
      description: 'Individuals needing more advanced support and tools.',
      price: '449,000đ',
      period: '/Month',
      buttonText: 'Get Started Now',
      buttonClass: 'premium-btn',
      features: [
        { text: 'Unlimited therapy sessions (book anytime, no limits)', included: true },
        { text: 'Golden gift set in community mode (skins, badges, wellness items)', included: true },
        { text: 'AI companion for real-time mental health tracking', included: true },
        { text: 'Priority booking with top-tier therapists', included: true },
        { text: '24/7 direct support', included: true },
        { text: 'All benefits from the Professional plan', included: true }
      ]
    }
  ];

  return (
    <div className="subscription-page page-with-header">
      <Header />
      
      <main className="subscription-main">
        <div className="subscription-container">
          <div className="pricing-cards">
            {loadingActive ? (
              <p>Loading plans...</p>
            ) : activeError ? (
              <div className="active-error">Failed to load plans. Showing default options.</div>
            ) : activePlans && activePlans.length > 0 ? (
              // When we have backend-driven plans, compute a master feature list so rows align
              activePlans.map((plan, index) => (
                <div
                  key={index}
                  className={`pricing-card ${plan.isHighlighted ? 'highlighted' : ''} ${plan.isPopular ? 'popular' : ''}`}
                >
                  <div className="card-header">
                    <h3 className="plan-name">{plan.name || plan.title}</h3>
                    <p className="plan-description">{plan.description || plan.summary}</p>
                  </div>

                  <div className="price-section">
                    <span className="price">{formatPrice(plan.priceText || plan.price || '')}</span>
                    <span className="period">{plan.period || ''}</span>
                  <span className="period">{getPlanPeriod(plan) || ''}</span>
                  </div>

                  <button
                    className={`cta-button ${plan.buttonClass || mapPlanNameToButtonClass(plan.name || plan.title)}`}
                    onClick={() => handleGetStarted(plan.name || plan.title)}
                  >
                    {plan.buttonText || 'Get Started'}
                  </button>

                  <div className="features-list">
                    {(plan.features || []).map((feature, featureIndex) => {
                      const text = getFeatureText(feature);
                      const included = getFeatureIncluded(feature);
                      return (
                        <div key={featureIndex} className={`feature-item ${included ? 'included' : 'excluded'}`}>
                          <span className="feature-icon">{included ? '✓' : '✗'}</span>
                          <span className="feature-text">{text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            ) : (
              // Fallback to the hard-coded local plans (keeps original styles/markup)
              plans.map((plan, index) => (
                <div 
                  key={index} 
                  className={`pricing-card ${plan.isHighlighted ? 'highlighted' : ''} ${plan.isPopular ? 'popular' : ''}`}
                >
                  <div className="card-header">
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-description">{plan.description}</p>
                  </div>
                  
                  <div className="price-section">
                    <span className="price">{formatPrice(plan.price)}</span>
                    <span className="period">{plan.period}</span>
                  </div>
                  
                  <button 
                    className={`cta-button ${plan.buttonClass}`}
                    onClick={() => handleGetStarted(plan.name)}
                  >
                    {plan.buttonText}
                  </button>
                  
                  <div className="features-list">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className={`feature-item ${feature.included ? 'included' : 'excluded'}`}>
                        <span className="feature-icon">
                          {feature.included ? '✓' : '✗'}
                        </span>
                        <span className="feature-text">{feature.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Subscription;
